import { useMutation, useQueryClient } from '@tanstack/react-query'
import { TRPCClientError } from '@trpc/client'
import { type ProcessedIcon } from '#app/lib/file-processor.ts'
import { trpc } from '#app/lib/trpc-tanstack.tsx'

// Transform ProcessedIcon[] to tRPC input format
export function transformIconsForUpload(icons: ProcessedIcon[]) {
	return icons.map((icon) => ({
		name: icon.name,
		path: icon.path,
		svg: icon.content, // tRPC expects 'svg' instead of 'content'
		optimizedSvg: icon.optimizedContent,
	}))
}

export function useCreateUploaded(options?: {
	onSuccess?: (data: { id: string; name: string }) => void
}) {
	const queryClient = useQueryClient()

	return useMutation({
		...trpc.uploaded.create.mutationOptions(),
		onSuccess: (data) => {
			void Promise.all([
				queryClient.invalidateQueries({
					queryKey: trpc.uploaded.pathKey(),
				}),
				queryClient.invalidateQueries({
					queryKey: trpc.iconSets.getPublicUploadedTree.queryKey(),
				}),
			])
			options?.onSuccess?.(data)
		},
		onError: (error) => {
			console.error('Error creating uploaded icon set:', error)
		},
	})
}

export function useUpdateUploaded(options?: {
	onSuccess?: (data: { id: string; name: string }) => void
}) {
	const queryClient = useQueryClient()

	return useMutation({
		...trpc.uploaded.update.mutationOptions(),
		onSuccess: (data) => {
			void Promise.all([
				queryClient.invalidateQueries({
					queryKey: trpc.uploaded.pathKey(),
				}),
				queryClient.invalidateQueries({
					queryKey: trpc.iconSets.getPublicUploadedTree.queryKey(),
				}),
			])
			options?.onSuccess?.(data)
		},
		onError: (error) => {
			console.error('Error updating uploaded icon set:', error)
		},
	})
}

export function useDeleteUploaded(options?: { onSuccess?: () => void }) {
	const queryClient = useQueryClient()

	return useMutation({
		...trpc.uploaded.delete.mutationOptions(),
		onSuccess: (_data, variables) => {
			// Remove the specific getById query for deleted ID to prevent 404 refetch
			queryClient.removeQueries({
				queryKey: trpc.uploaded.getById.queryKey({ id: variables.id }),
				exact: false,
			})

			void Promise.all([
				queryClient.invalidateQueries({
					queryKey: trpc.uploaded.pathKey(),
				}),
				queryClient.invalidateQueries({
					queryKey: trpc.iconSets.getPublicUploadedTree.queryKey(),
				}),
			])
			options?.onSuccess?.()
		},
		onError: (error) => {
			console.error('Error deleting uploaded icon set:', error)
		},
	})
}

// Helper function to check if error is a TRPC error with specific code
function isTRPCErrorWithCode(
	error: unknown,
	code: string,
): error is TRPCClientError<any> {
	return error instanceof TRPCClientError && error.data?.code === code
}

// Helper function to format error messages
export function getUploadErrorMessage(error: unknown): string {
	// Handle TRPC errors with specific codes
	if (isTRPCErrorWithCode(error, 'CONFLICT')) {
		return (error as TRPCClientError<any>).message
	}
	if (isTRPCErrorWithCode(error, 'BAD_REQUEST')) {
		return (error as TRPCClientError<any>).message
	}
	if (isTRPCErrorWithCode(error, 'NOT_FOUND')) {
		return 'Icon set not found'
	}
	if (isTRPCErrorWithCode(error, 'UNAUTHORIZED')) {
		return 'Please log in to manage icon sets'
	}

	// Fallback to generic error handling
	if (error instanceof Error) {
		return error.message
	}

	return 'An unexpected error occurred'
}
